import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { statusLabel, statusTone } from "../lib/format";
import { toast } from "sonner";
import { useFeatures } from "../context/FeatureContext";
import { Pencil, CornerUpLeft, LayoutGrid } from "lucide-react";

import { useAuth } from "../context/AuthContext";
import EditProjectModal from "../components/EditProjectModal";
import DesignImage from "../components/DesignImage";
import EnrichFromExcelDialog from "../components/EnrichFromExcelDialog";
import KanbanRiskPill from "./pm/KanbanRiskPill";
import { systemTypeLabel } from "./Projects";
import Overview from "./projectDetail/Overview";
import DesignTab from "./projectDetail/Design";
import QuoteTab from "./projectDetail/Quote";
import TasksTab from "./projectDetail/Tasks";
import DocsTab from "./projectDetail/Docs";
import MonitoringTab from "./projectDetail/Monitoring";
import PerformanceReport from "./projectDetail/PerformanceReport";
import ActualIncomePanel from "../components/ActualIncomePanel";
import AuditTab from "./projectDetail/Audit";
import LifecycleTimeline from "./projectDetail/LifecycleTimeline";
import MaintenanceTab from "./projectDetail/Maintenance";
import InvestorModel from "./projectDetail/InvestorModel";
import SubSitesPanel from "./projectDetail/SubSitesPanel";
import MetersPanel from "./projectDetail/MetersPanel";
import ProjectAssets from "./projectDetail/ProjectAssets";
import ProjectOpex from "./projectDetail/ProjectOpex";
import SiteRatesPanel from "./projectDetail/SiteRatesPanel";

const TABS = [
  { key: "Overview" },
  { key: "Design" },
  { key: "Financials", feature: "quotes" },
  { key: "Investor Model", feature: "investor_model" },
  { key: "Site Rates", feature: "monitoring" },
  { key: "Lifecycle" },
  { key: "Tasks" },
  { key: "Documents", feature: "documents" },
  { key: "Monitoring", feature: "monitoring" },
  { key: "Performance", feature: "monitoring" },
  { key: "Meters" },
  { key: "Assets" },
  { key: "OPEX" },
  { key: "Maintenance" },
  { key: "Audit" },
];

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [design, setDesign] = useState(null);
  const [quote, setQuote] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [docs, setDocs] = useState([]);
  const [monitoring, setMonitoring] = useState(null);
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => TABS.find((t) => t.key.toLowerCase() === (searchParams.get("tab") || "").toLowerCase())?.key || "Overview");
  const { canUse } = useFeatures();
  const visibleTabs = TABS.filter((t) => !t.feature || canUse(t.feature));
  const [editing, setEditing] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const canDelete = ["admin", "manager"].includes(user?.role);

  const loadAll = useCallback(async () => {
    try {
      const [p, d, q, t, doc, m] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/designs/by-project/${id}`),
        api.get(`/quotes/by-project/${id}`),
        api.get(`/tasks/by-project/${id}`),
        api.get(`/documents/by-project/${id}`),
        api.get(`/monitoring/by-project/${id}`),
      ]);
      setProject(p.data); setDesign(d.data); setQuote(q.data);
      setTasks(t.data); setDocs(doc.data); setMonitoring(m.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (!project) {
    return <div className="p-10 muted font-mono text-xs uppercase tracking-widest">Loading project…</div>;
  }

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] mx-auto" data-testid="project-detail-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <Link to="/projects" className="text-xs underline muted" data-testid="back-to-projects">← All projects</Link>
          <div className="label mt-2">{project.postcode || project.country}</div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">{project.name}</h1>
          {/* Breadcrumb to parent site — shows only when this project has one. */}
          {project.parent_project_id && (
            <div className="mt-2 text-xs muted flex items-center gap-1.5"
                 data-testid="project-parent-breadcrumb">
              <CornerUpLeft size={11} />
              <span className="font-mono uppercase tracking-widest">Sub-site of</span>
              <Link to={`/projects/${project.parent_project_id}`}
                    className="underline hover:text-[var(--accent)]"
                    data-testid="project-parent-link">
                view parent site →
              </Link>
            </div>
          )}
          <div className="flex items-center gap-3 mt-2">
            <span className={`tag ${statusTone(project.status)}`} data-testid="project-status">{statusLabel(project.status)}</span>
            <KanbanRiskPill
              projectId={project.id}
              value={project.risk_status}
              onChange={async (nextKey) => {
                const prev = project.risk_status;
                setProject(p => ({ ...p, risk_status: nextKey }));
                try {
                  await api.patch(`/projects/${project.id}`, { risk_status: nextKey });
                  toast.success(`Risk set to ${nextKey.charAt(0).toUpperCase() + nextKey.slice(1)}`);
                } catch (e) {
                  toast.error(formatApiError(e.response?.data?.detail) || "Could not update risk");
                  setProject(p => ({ ...p, risk_status: prev }));
                }
              }}
            />
            {project.data_source && (
              <span className="tag signal" data-testid="project-system-type">
                {systemTypeLabel(project.data_source)}
                {project.data_source === "solaredge" && project.solaredge_site_id
                  ? ` · ${project.solaredge_site_id}`
                  : ""}
              </span>
            )}
            {!project.data_source && project.solaredge_site_id && (
              <span className="muted text-xs font-mono">SE site: {project.solaredge_site_id}</span>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="label">Customer</div>
          <div className="font-medium">{project.customer_name}</div>
          <div className="muted text-xs">{project.customer_email || "—"}</div>
          <button onClick={() => setEditing(true)} className="btn-ghost mt-3" data-testid="project-edit-btn">
            <Pencil size={13} /> Edit project
          </button>
          <button onClick={() => setEnriching(true)} className="btn-ghost mt-2" data-testid="project-enrich-btn"
                  title="Fill in missing fields from an Excel/CSV — in-app values are never overwritten.">
            <Pencil size={13} /> Import from Excel
          </button>
        </div>
      </div>

      <DesignImage
        project={project}
        editable={canDelete}
        onChange={(url) => setProject((p) => ({ ...p, design_image_url: url }))}
        className="mb-6 max-w-md"
      />

      {editing && (
        <EditProjectModal
          project={project}
          canDelete={canDelete}
          onClose={() => setEditing(false)}
          onSaved={loadAll}
          onDeleted={() => nav("/projects", { replace: true })}
        />
      )}
      {enriching && (
        <EnrichFromExcelDialog
          project={project}
          onClose={() => setEnriching(false)}
          onApplied={() => { setEnriching(false); loadAll(); }}
        />
      )}

      {/* Sub-sites overview — one panel showing every direct child + a
          quick roll-up total. Only rendered when the project actually
          has children (avoids empty state noise for leaf sites). */}
      <SubSitesPanel projectId={id} project={project} />

      <div className="border-b border-[var(--line)] flex gap-1 mb-8 overflow-x-auto" data-testid="project-tabs">
        {visibleTabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key.toLowerCase()}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key ? "border-[var(--ink)] text-[var(--ink)]" : "border-transparent muted hover:text-[var(--ink)]"
            }`}
          >{t.key}</button>
        ))}
      </div>

      {tab === "Overview" && <Overview project={project} design={design} quote={quote} tasks={tasks} monitoring={monitoring} reload={loadAll} />}
      {tab === "Design" && <DesignTab project={project} design={design} onSaved={loadAll} />}
      {tab === "Financials" && canUse("quotes") && (
        <div className="space-y-6">
          <QuoteTab project={project} design={design} quote={quote} onSaved={loadAll} />
          <ActualIncomePanel projectId={id} project={project} />
        </div>
      )}
      {tab === "Investor Model" && canUse("investor_model") && <InvestorModel project={project} design={design} quote={quote} onSaved={loadAll} />}
      {tab === "Site Rates" && canUse("monitoring") && <SiteRatesPanel project={project} onSaved={loadAll} />}
      {tab === "Lifecycle" && <LifecycleTimeline projectId={id} />}
      {tab === "Tasks" && <TasksTab projectId={id} tasks={tasks} onSaved={loadAll} />}
      {tab === "Documents" && canUse("documents") && <DocsTab projectId={id} docs={docs} onSaved={loadAll} />}
      {tab === "Monitoring" && canUse("monitoring") && <MonitoringTab projectId={id} project={project} design={design} monitoring={monitoring} reload={loadAll} />}
      {tab === "Performance" && canUse("monitoring") && <PerformanceReport projectId={id} />}
      {tab === "Meters" && <MetersPanel projectId={id} />}
      {tab === "Assets" && <ProjectAssets projectId={id} />}
      {tab === "OPEX" && <ProjectOpex projectId={id} />}
      {tab === "Maintenance" && <MaintenanceTab project={project} reload={loadAll} />}
      {tab === "Audit" && <AuditTab projectId={id} />}
    </div>
  );
}
